import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Loader2, Lock, AlertTriangle, CheckCircle2, Eye, ExternalLink, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Self-Complete Pre-Authorization modal.
 *
 * Shown only to recruiters whom an admin has whitelisted (and to admins
 * directly). Replaces the "email the student" flow:
 *   1. Big "Open Helcim Pre-Authorization" button opens the correct
 *      subscription URL in a new tab.
 *   2. Recruiter types the student's full name — rendered live in Great
 *      Vibes cursive on page 3 of the declaration PDF.
 *   3. Two attestation checkboxes gate the Submit button.
 *   4. Submit → server overlays signature + date, saves PDF against the
 *      student's Documents list, writes an audit entry, locks the flow.
 */
export default function SelfCompletePreAuthModal({ open, onClose, student, onCompleted }) {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signature, setSignature] = useState('');
  const [attestation, setAttestation] = useState(false);
  const [helcimDone, setHelcimDone] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (!open || !student?.id) return;
    let cancelled = false;
    setMeta(null);
    setSignature(student.full_name || '');
    setAttestation(false);
    setHelcimDone(false);
    setShowPdf(false);
    setPdfUrl('');
    setLoading(true);
    axios.get(`${API}/students/${student.id}/pre-authorization/meta`)
      .then((r) => { if (!cancelled) setMeta(r.data); })
      .catch((e) => {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || 'Could not load Pre-Authorization details');
          onClose?.();
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, student?.id]);

  const handlePreviewPdf = async () => {
    if (showPdf) {
      setShowPdf(false);
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl('');
      return;
    }
    if (!signature.trim()) {
      toast.error('Type the student name first — the preview shows it stamped on the PDF.');
      return;
    }
    setPreviewing(true);
    try {
      const url = `${API}/students/${student.id}/pre-authorization/self-complete/preview?signature=${encodeURIComponent(signature.trim())}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfUrl(blobUrl);
      setShowPdf(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the signed-PDF preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSubmit = async () => {
    if (!meta?.price_supported) return;
    if (!signature.trim()) { toast.error('Type the student name first.'); return; }
    if (!attestation) { toast.error('Tick the attestation box to confirm authorization.'); return; }
    if (!helcimDone) { toast.error('Confirm you completed the Helcim pre-authorization first.'); return; }
    const ok = window.confirm(
      `Submit Pre-Authorization for ${meta.student_name || student?.full_name}?\n\n`
      + `This action is final — the signed declaration will be stored against the student record, audit-logged with your user id and IP, and the Pre-Auth row will lock.`
    );
    if (!ok) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/students/${student.id}/pre-authorization/self-complete`,
        {
          typed_signature: signature.trim(),
          attestation_confirmed: true,
          helcim_confirmed: true,
        },
      );
      toast.success(`Pre-Authorization completed · ${data.amount_text}`);
      onCompleted?.(data);
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl('');
    setShowPdf(false);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="self-pre-auth-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>
            <ShieldCheck size={18} className="text-emerald-700" />
            Self-Complete Pre-Authorization
            <Badge variant="outline" className="ml-2 text-[11px] bg-emerald-50 border-emerald-200 text-emerald-800">
              <Lock size={11} className="mr-1" /> Audit-logged
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-xs">
            You're signing the student declaration on the student's behalf. Every action is attestation-gated and tamper-logged with your user id, IP, and timestamp.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}

        {!loading && meta && !meta.price_supported && (
          <div className="flex items-start gap-3 p-4 bg-rose-50 border border-rose-200 rounded-lg" data-testid="self-pre-auth-unsupported-price">
            <AlertTriangle size={20} className="text-rose-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-rose-900">Sale price is not in the Helcim tier list</p>
              <p className="text-rose-800 mt-1">
                This student's sale price is <span className="font-mono">C${(meta.sale_price || 0).toLocaleString()}</span>.
                Supported: {(meta.supported_prices || []).map(p => `C$${p.toLocaleString()}`).join(', ')}.
              </p>
            </div>
          </div>
        )}

        {!loading && meta && (meta.already_sent || meta.self_completed_at) && (
          <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="self-pre-auth-already-done">
            <CheckCircle2 size={20} className="text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-emerald-900">Already completed</p>
              <p className="text-emerald-800 mt-1">
                {meta.self_completed_at ? (
                  <>Self-completed on <span className="font-mono">{(meta.self_completed_at || '').slice(0, 10)}</span>{meta.self_completed_by_email && <> by <span className="font-mono">{meta.self_completed_by_email}</span></>}.</>
                ) : (
                  <>Email was sent on <span className="font-mono">{(meta.sent_at || '').slice(0, 10)}</span>.</>
                )}
              </p>
            </div>
          </div>
        )}

        {!loading && meta && meta.price_supported && !meta.already_sent && !meta.self_completed_at && (
          <div className="space-y-4">
            <div className="grid grid-cols-[110px_1fr] gap-2 text-sm">
              <div className="text-gray-500 font-medium">Student</div>
              <div className="font-medium text-gray-900">{meta.student_name} <span className="text-gray-500 font-mono text-xs">· {meta.student_email}</span></div>

              <div className="text-gray-500 font-medium">Amount</div>
              <div className="font-mono text-gray-900">{meta.amount_text}
                <span className="text-gray-500 text-xs ml-1">(60% of C${(meta.sale_price || 0).toLocaleString()} + C$250)</span>
              </div>
            </div>

            {/* Step 1 — Helcim */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 mb-2">Step 1 — Pre-authorize on Helcim</p>
              <p className="text-xs text-blue-800 mb-3">
                Open the student's correct Helcim subscription page in a new tab and complete the pre-authorization using the student's card. The link below is the configured URL for their sale price — it cannot be edited.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <a
                  href={meta.helcim_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-full px-4 py-2 text-xs font-semibold"
                  data-testid="self-pre-auth-helcim-link"
                >
                  <ExternalLink size={13} /> Open Helcim Pre-Authorization
                </a>
                <span className="text-[11px] font-mono text-blue-700 truncate max-w-[320px]">{meta.helcim_link}</span>
              </div>
              <label className="flex items-start gap-2 mt-3 text-xs text-blue-900 cursor-pointer">
                <Checkbox
                  checked={helcimDone}
                  onCheckedChange={(v) => setHelcimDone(!!v)}
                  data-testid="self-pre-auth-helcim-done"
                />
                <span>I've completed the Helcim pre-authorization for this student on Helcim's hosted page.</span>
              </label>
            </div>

            {/* Step 2 — Sign the declaration */}
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm font-semibold text-emerald-900 mb-2">Step 2 — Sign the declaration</p>
              <p className="text-xs text-emerald-800 mb-3">
                Type the student's full legal name. It will be rendered in cursive on page 3 of the declaration, alongside today's date. Section 4 is pre-filled with {meta.amount_text}.
              </p>
              <Input
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Student's full legal name"
                maxLength={120}
                className="text-sm font-serif italic"
                data-testid="self-pre-auth-signature-input"
              />
              <div className="flex items-center gap-2 mt-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-3 text-xs"
                  onClick={handlePreviewPdf}
                  disabled={previewing || !signature.trim()}
                  data-testid="self-pre-auth-preview-pdf"
                >
                  {previewing ? <><Loader2 size={12} className="mr-1 animate-spin" /> Loading…</> : <><Eye size={12} className="mr-1" /> {showPdf ? 'Hide preview' : 'Preview signed PDF'}</>}
                </Button>
                <span className="text-xs text-emerald-700">Signed-at date: today ({new Date().toISOString().slice(0, 10)})</span>
              </div>
              {showPdf && pdfUrl && (
                <iframe
                  src={pdfUrl}
                  title="Signed declaration preview"
                  className="w-full h-[480px] border border-emerald-200 rounded-md mt-3 bg-white"
                  data-testid="self-pre-auth-pdf-iframe"
                />
              )}
            </div>

            {/* Step 3 — Attestation */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-semibold text-amber-900 mb-2">Step 3 — Attestation</p>
              <label className="flex items-start gap-2 text-xs text-amber-900 cursor-pointer">
                <Checkbox
                  checked={attestation}
                  onCheckedChange={(v) => setAttestation(!!v)}
                  data-testid="self-pre-auth-attestation"
                />
                <span>
                  I confirm the student has authorized me to pre-authorize payment and sign the declaration on their behalf. I understand this action is permanently logged with my user id and IP.
                </span>
              </label>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={close} data-testid="self-pre-auth-cancel">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={
              submitting
              || loading
              || !meta
              || !meta.price_supported
              || meta.already_sent
              || meta.self_completed_at
              || !signature.trim()
              || !attestation
              || !helcimDone
            }
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="self-pre-auth-submit"
          >
            {submitting ? <><Loader2 size={14} className="mr-1 animate-spin" /> Submitting…</> : <><ShieldCheck size={14} className="mr-1" /> Submit &amp; Lock</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
